import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ORDER_STATUS_IN_PROGRESS, YOUR_APPOINTMENTS } from 'features/order-tracker/services/constants';
import { LivePersonChat } from 'features/order-tracker/shared-components';
import { CHAT_BUTTON_PURPLE_SECONDARY } from 'features/order-tracker/shared-components/LivePersonChat/constants';
import { LocationIcon } from 'features/order-tracker/shared-components/svg';
import { AppointmentAnalyticService } from 'features/order-tracker/store/sagas/services/appointmentAnalyticsService';
import { APPOINTMENT_AMENDABLE_STATUSES } from 'features/order-tracker/store/sagas/services/helpers/constants';
import {
  getAppointmentLivePersonProps,
  getAppointmentsInfo,
  getStatuses,
} from 'features/order-tracker/store/selectors';
import { AQA_ELEMENTS_MARKER_LIST } from 'features/order-tracker/utils/aqaElementsIDList';
import { OPENREACH_APPOINTMENT_TYPES, ORDER_STATUSES } from 'features/order-tracker/utils/entries';

import { AppointmentCard } from './components';
import {
  ADDRESS_LABEL,
  EMAIL_ADDRESS_LABEL,
  INSTALATION_ADDRESS_TITLE_ALT,
  SITE_CONTACT_LABEL,
  UPDATE_DETAILS_BUTTON_LABEL,
} from './constants';

import styles from './styles.module.scss';

export const AppointmentsColumn: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { engineerAppointments } = useSelector(getAppointmentsInfo);
  const livePersonDivId = useSelector(getAppointmentLivePersonProps);
  const { orderStatuses, orderStatusFromGOC } = useSelector(getStatuses);
  const orderStatus = orderStatusFromGOC || orderStatuses[0];
  const [analytics, setAnalytics] = useState<components.AppointmentCardAnalytics>();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isOpenreachApptToday, setIsOpenreachApptToday] = useState<boolean | null>(null);
  const [currentAppointment, setCurrentAppointment] = useState(engineerAppointments?.[currentIndex]);
  const { installationAddress, email, siteContact, type, status, isAppointmentToday } = currentAppointment || {};

  if (!engineerAppointments.length) {
    return null;
  }

  useEffect(() => {
    const openreachAppointmentWhichIsToday =
      OPENREACH_APPOINTMENT_TYPES.includes(type) &&
      APPOINTMENT_AMENDABLE_STATUSES.includes(status) &&
      ORDER_STATUSES[orderStatus] === ORDER_STATUS_IN_PROGRESS &&
      isAppointmentToday;
    setIsOpenreachApptToday(openreachAppointmentWhichIsToday);
  }, [currentAppointment]);

  useEffect(() => {
    analytics &&
      AppointmentAnalyticService.trackAppointmentEvent({
        ref: containerRef,
        value: `Appt - ${analytics.appointmentStatus}:Amend possible - ${analytics.amendPossible}:
      On day appointment - ${analytics.appointmentStage}`,
      });
  }, [analytics]);

  const previousAppt = engineerAppointments[currentIndex <= 0 ? -1 : currentIndex - 1];
  const nextAppt = engineerAppointments[currentIndex === engineerAppointments.length - 1 ? -1 : currentIndex + 1];

  const handlePreviousBtnClick = (): void => {
    setCurrentIndex(currentIndex - 1);
    setCurrentAppointment(engineerAppointments?.[currentIndex - 1]);
  };

  const handleNextBtnClick = (): void => {
    setCurrentIndex(currentIndex + 1);
    setCurrentAppointment(engineerAppointments?.[currentIndex + 1]);
  };

  return (
    <div ref={containerRef} className={styles.appointmentsContainer}>
      <h3>{YOUR_APPOINTMENTS}</h3>
      <div className={styles.navigationBtnSection}>
        {!!previousAppt && (
          <button onClick={handlePreviousBtnClick} className={styles.btnPrevious} type="button">
            {previousAppt.displayTitle}
          </button>
        )}
        {!!nextAppt && (
          <button onClick={handleNextBtnClick} className={styles.btnNext} type="button">
            {nextAppt.displayTitle}
          </button>
        )}
      </div>
      {isOpenreachApptToday && (
        <div className={styles.todaysApptLocationInfo}>
          <LocationIcon />
          <h5>{INSTALATION_ADDRESS_TITLE_ALT}</h5>
          <p className={styles.address}>
            {ADDRESS_LABEL}
            <span className={styles.boldText}>{installationAddress}</span>
          </p>
          <p className={styles.email}>
            {EMAIL_ADDRESS_LABEL}
            <span className={styles.boldText}>{email}</span>
          </p>
          <p className={styles.siteContact}>
            {SITE_CONTACT_LABEL}
            <span className={styles.boldText}>{siteContact}</span>
          </p>
          <LivePersonChat
            livePersonId={livePersonDivId}
            chatType={CHAT_BUTTON_PURPLE_SECONDARY}
            analytics={UPDATE_DETAILS_BUTTON_LABEL}
            altLabel={UPDATE_DETAILS_BUTTON_LABEL}
          />
        </div>
      )}
      <AppointmentCard
        installationAddress={installationAddress}
        key={currentAppointment.orderItemReference}
        id={`${AQA_ELEMENTS_MARKER_LIST.summaryPage.appointments.appointmentInfo}-${currentIndex}`}
        appointment={currentAppointment}
        setAnalytics={setAnalytics}
        isOpenreachApptToday={isOpenreachApptToday}
      />
    </div>
  );
};
